import { Modal, useModals } from 'almanac-ui/Modal'
import { useState } from 'react'
import { voiceParams } from './AddVoice/Step2'
import { Step3 } from './AddVoice/Step3'
import { VoiceData, VoiceParams } from 'hooks/useBrandVoices'
import { useUpdateBrandVoice } from 'hooks/queries/workspaces/brandVoices/useUpdateBrandVoice'
import { Heading } from 'blaze-ui/Heading/Heading'
import { HighDetailedVoice } from 'blaze-ui/icons/24'
import Styles from './EditVoiceModal.module.scss'
import { Button } from 'blaze-ui/Button'
import { useCreateBrandVoice } from 'hooks/queries/workspaces/brandVoices/useCreateBrandVoice'
import { ArrowRight } from 'blaze-ui/icons'
import { useId } from '@react-aria/utils'

type PartialVoice = Partial<Omit<VoiceData, 'voiceParams'>> & { voice_params: voiceParams }

interface AddVoiceModalProps {
  voiceData: PartialVoice
  brandKitId?: number
  onSuccess?: () => void
  onComplete?: (voiceId?: number) => void
  onCreate: ({ voiceId }: { voiceId: number }) => void
  onBack?: () => void
}

const UpdateButton = ({ voiceData, onSuccess }: { voiceData: VoiceData; onSuccess?: () => void }) => {
  const { closeModal } = useModals()
  const { mutateAsync } = useUpdateBrandVoice({ id: voiceData.id })
  return (
    <Button
      size="lg"
      isDisabled={!voiceData.voice_params.name.length}
      onPress={async () => {
        await mutateAsync({ voice_params: voiceData.voice_params })
        closeModal()
        onSuccess?.()
      }}
    >
      Save Voice
    </Button>
  )
}

const CreateButton = ({
  voiceParams,
  brandKitId,
  onSuccess,
  onCreate,
}: {
  voiceParams: VoiceParams
  brandKitId?: number
  onSuccess?: () => void
  onCreate: ({ voiceId }: { voiceId: number }) => void
}) => {
  const { mutateAsync } = useCreateBrandVoice()
  return (
    <Button
      size="lg"
      isDisabled={!voiceParams.name.length}
      onPress={async () => {
        const result = await mutateAsync({ voiceParams, brandKitId })
        onCreate({ voiceId: result.id })
        onSuccess?.()
      }}
      endIcon={ArrowRight}
      aria-label="Create Voice"
    >
      Create Voice
    </Button>
  )
}

export const EditVoiceModal = ({
  voiceData,
  brandKitId,
  onComplete,
  onSuccess,
  onCreate,
  onBack,
}: AddVoiceModalProps) => {
  const { closeModal: closeAlmanacModal } = useModals()
  const [voice, setVoice] = useState<PartialVoice>(voiceData)
  const id = useId()
  const closeModal = (voiceId?: number) => {
    onComplete?.(voiceId)

    closeAlmanacModal()
  }

  const handleBack = onBack ? onBack : closeAlmanacModal

  return (
    <Modal.Root size="sm" onClose={closeModal} aria-labelledby={id} blaze>
      <Modal.Header size="lg" onClose={onBack || closeAlmanacModal}>
        <div className={Styles.heading}>
          <HighDetailedVoice size={32} />
          <Heading level={3} id={id}>
            Edit Voice
          </Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <Step3
          key="step3"
          voiceParams={voice?.voice_params || {}}
          onChange={(vp) => setVoice((voice) => ({ ...voice, voice_params: vp }))}
          editMode={true}
        />
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button onPress={handleBack} size="lg" variant="tertiary">
            Back
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent>
          {'id' in voice ? (
            <UpdateButton voiceData={voice as VoiceData} onSuccess={onSuccess} />
          ) : (
            <CreateButton
              voiceParams={voice.voice_params}
              brandKitId={brandKitId}
              onSuccess={onSuccess}
              onCreate={onCreate}
            />
          )}
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
